// D-094 (owner, 2026-10-29; design A "Border burst", picked on the Claude Design canvas): the first
// time anyone opens a new shop's admin, confetti bursts in from all four borders and a card says the
// shop is live. Once per shop: the store summary's `celebrate` stays true until POST …/celebrated
// (store_settings.live_celebrated_at, shard 1045; shops older than that count as shown). The card is
// the shared Popup (native dialog); the confetti is a manual popover opened after it, so it flies
// over the card in the top layer and never takes a click. Reduced motion: the card, no confetti.
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { Icon, Popup } from "@ady/ui";
import { CopyButton } from "../marketing/ui";
import { sfApi, sfKeys, storeAddress, useSf, type StoreSummary } from "../modules/storefront/api";
import { useStoreIds } from "../modules/storefront/common";
import { AppLink } from "./AppLink";
import { useShell } from "./context";

const COLORS = [
  "var(--ady-color-brand-blue)",
  "var(--ady-color-brand-green)",
  "var(--ady-color-chart-2)",
  "var(--ady-color-chart-3)",
  "var(--ady-color-chart-4)",
  "var(--ady-color-chart-5)",
  "var(--ady-amber-300)",
];
/** width, height, corner radius (px): paper strips, dots, squares. */
const SHAPES: [number, number, string][] = [
  [8, 12, "2px"],
  [9, 9, "50%"],
  [4, 18, "2px"],
  [10, 10, "2px"],
  [6, 6, "50%"],
];

interface Piece {
  x: number;
  y: number;
  /** Where the burst peaks (55 %) and where the piece ends, relative to its start, in px. */
  mid: [number, number];
  end: [number, number];
  axis: string;
  spin: number;
  duration: number;
  delay: number;
  w: number;
  h: number;
  radius: string;
  color: string;
}

/** 0–1 from the platform CSPRNG (the codebase bans Math.random). */
const random = () => crypto.getRandomValues(new Uint32Array(1))[0]! / 2 ** 32;
const rand = (a: number, b: number) => a + (b - a) * random();
const pick = <T,>(xs: T[]) => xs[Math.floor(random() * xs.length)]!;

/** Pieces from the four borders in turn: top and bottom shoot in and fall, the sides arc inward. */
function makePieces(width: number, height: number): Piece[] {
  const n = width < 640 ? 90 : 160;
  const reach = Math.min(520, width * 0.45);
  return Array.from({ length: n }, (_, i) => {
    const side = random() < 0.5 ? 1 : -1;
    let p: Pick<Piece, "x" | "y" | "mid" | "end">;
    if (i % 4 === 0) {
      const dx = side * rand(10, 140);
      const dy = rand(160, 420);
      p = { x: rand(0, width), y: -12, mid: [dx, dy], end: [dx * 1.4, dy * 1.9] };
    } else if (i % 4 === 1) {
      const dx = side * rand(10, 150);
      const dy = -Math.min(rand(260, 560), height * 0.8);
      p = { x: rand(0, width), y: height + 12, mid: [dx, dy], end: [dx * 1.35, dy * 0.1] };
    } else {
      const fromLeft = i % 4 === 2;
      const dx = (fromLeft ? 1 : -1) * rand(200, reach);
      const rise = rand(60, 220);
      p = {
        x: fromLeft ? -12 : width + 12,
        y: rand(0.08, 0.96) * height,
        mid: [dx, -rise],
        end: [dx * 1.25, rise * 1.7],
      };
    }
    const [w, h, radius] = pick(SHAPES);
    return {
      ...p,
      axis: `${rand(0, 1).toFixed(2)}, ${rand(0.2, 1).toFixed(2)}, ${rand(0, 0.5).toFixed(2)}`,
      spin: side * rand(540, 1440),
      duration: rand(2400, 3600),
      delay: rand(0, 350),
      w,
      h,
      radius,
      color: pick(COLORS),
    };
  });
}

function Confetti({ onDone }: { onDone: () => void }) {
  const layer = useRef<HTMLDivElement>(null);
  const [pieces] = useState(() => makePieces(window.innerWidth, window.innerHeight));
  useEffect(() => {
    const el = layer.current;
    if (!el) return;
    el.showPopover?.();
    const runs = [...el.children].map((node, i) => {
      const p = pieces[i]!;
      const at = (x: number, y: number, turn: number) =>
        `translate(${x}px, ${y}px) rotate3d(${p.axis}, ${turn}deg)`;
      return (node as HTMLElement).animate(
        [
          { transform: at(0, 0, 0), opacity: 1, easing: "cubic-bezier(.15,.75,.35,1)" },
          {
            transform: at(p.mid[0], p.mid[1], p.spin * 0.6),
            opacity: 1,
            offset: 0.55,
            easing: "cubic-bezier(.45,0,.85,.6)",
          },
          { transform: at(p.end[0], p.end[1], p.spin), opacity: 0 },
        ],
        { duration: p.duration, delay: p.delay, fill: "both" },
      );
    });
    // A cancelled run (unmount, or StrictMode's second effect pass) settles `finished` too: only a
    // run that is still current may end the burst.
    let current = true;
    void Promise.allSettled(runs.map((a) => a.finished)).then(() => current && onDone());
    return () => {
      current = false;
      runs.forEach((a) => a.cancel());
    };
  }, [pieces, onDone]);
  return (
    <div
      ref={layer}
      popover="manual"
      aria-hidden="true"
      data-testid="live-confetti"
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100dvh",
        maxWidth: "none",
        maxHeight: "none",
        margin: 0,
        padding: 0,
        border: 0,
        background: "transparent",
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            left: p.x,
            top: p.y,
            width: p.w,
            height: p.h,
            borderRadius: p.radius,
            background: p.color,
          }}
        />
      ))}
    </div>
  );
}

export function LiveCelebration() {
  const { orgId, storeId } = useStoreIds();
  const store = useSf(sfKeys.store(orgId, storeId), () => sfApi.store(orgId, storeId));
  // Latched: marking it seen makes the next refetch say `celebrate: false`, and the card must stay
  // until the vendor closes it.
  const [shownFor, setShownFor] = useState<string | null>(null);
  const due = !!store.data?.celebrate && store.data.status === "live";
  useEffect(() => {
    if (due) setShownFor(storeId);
  }, [due, storeId]);
  return shownFor === storeId && store.data ? (
    <Celebrate key={storeId} store={store.data} onClosed={() => setShownFor(null)} />
  ) : null;
}

function Celebrate({ store, onClosed }: { store: StoreSummary; onClosed: () => void }) {
  const { orgId, storeId } = useStoreIds();
  const { orgSlug, storeSlug } = useShell();
  const qc = useQueryClient();
  const [open, setOpen] = useState(true);
  const [burst, setBurst] = useState(
    () => !window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  const burstDone = useCallback(() => setBurst(false), []);
  // The main action takes focus (the Popup's showModal ran in its own effect, just before this one).
  const view = useRef<HTMLAnchorElement>(null);
  useEffect(() => view.current?.focus(), []);
  // Seen as soon as it shows, so a reload or another tab never plays it twice.
  useEffect(() => {
    void sfApi.celebrated(orgId, storeId).catch(() => undefined);
  }, [orgId, storeId]);
  const close = () => {
    setOpen(false);
    qc.setQueryData<StoreSummary>(sfKeys.store(orgId, storeId), (s) =>
      s ? { ...s, celebrate: false } : s,
    );
  };
  // Unmounted once the card is closed and the confetti has landed.
  useEffect(() => {
    if (!open && !burst) onClosed();
  }, [open, burst, onClosed]);
  const address = storeAddress(store);
  const host = address.replace(/^https:\/\//, "").replace(/\/$/, "");
  return (
    <>
      <Popup
        open={open}
        onClose={close}
        label={t("storefront.admin.celebrate.title")}
        size="sm"
        data-testid="live-celebration"
      >
        <div className="flex flex-col items-center gap-3 px-1 pt-2 pb-1 text-center">
          <span
            className="flex size-14 items-center justify-center rounded-full"
            style={{ background: "var(--ady-gradient-brand)", color: "white" }}
          >
            <Icon name="store" />
          </span>
          {/* The approved design's large title; admin headings are 12 px by rule, so this is text and
              the popup's label carries the name. */}
          <p className="text-title-sm font-semibold text-fg">
            {t("storefront.admin.celebrate.title")}
          </p>
          <p className="text-sm text-fg-secondary">{t("storefront.admin.celebrate.body")}</p>
          <div className="flex w-full min-w-0 items-center gap-1 rounded-xs border border-card bg-surface py-1 ps-2.5 pe-1">
            <span className="min-w-0 flex-1 truncate text-start text-sm font-semibold text-fg">
              {host}
            </span>
            <CopyButton text={address} label={t("storefront.admin.celebrate.copy")} />
          </div>
          <div className="grid w-full gap-1.5 sm:grid-cols-2">
            <a
              ref={view}
              href={address}
              target="_blank"
              rel="noopener"
              onClick={close}
              className="flex min-h-control items-center justify-center gap-1.5 rounded-xs bg-primary px-3 text-sm font-semibold text-on-primary no-underline hover:bg-primary-hover"
              data-testid="live-view-shop"
            >
              <Icon name="external" />
              {t("storefront.admin.celebrate.view")}
            </a>
            <AppLink
              href={`/${orgSlug}/${storeSlug}/products`}
              onClick={close}
              className="flex min-h-control items-center justify-center rounded-xs border border-card px-3 text-sm font-semibold text-fg no-underline hover:bg-hover"
            >
              {t("storefront.admin.celebrate.addProduct")}
            </AppLink>
          </div>
        </div>
        <button
          type="button"
          aria-label={t("storefront.admin.celebrate.close")}
          onClick={close}
          className="absolute end-2 top-2 inline-flex size-7 items-center justify-center rounded-xs text-fg-secondary hover:bg-hover hover:text-fg"
        >
          <Icon name="x" />
        </button>
      </Popup>
      {burst ? <Confetti onDone={burstDone} /> : null}
    </>
  );
}
